import type { AddCustomQuestionToQuestionnairePayload } from '@/lib/api/questionnaires';
import type { JsonQuestion, JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import { getActiveQuestionCount } from '@/components/questionnaires/json-driven/questionMetrics';
import {
  buildQuestionOptions,
  getResponseTypeInputType,
  responseTypeRequiresOptions,
  type AdditionalQuestionResponseType,
} from '@/components/questionnaires/review/additionalQuestionConfig';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import LunchDiningRoundedIcon from '@mui/icons-material/LunchDiningRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import WavingHandRoundedIcon from '@mui/icons-material/WavingHandRounded';
import type { QuestionnaireSectionGroup, QuestionTypeChipStyle } from './types';

export function stripTriggerText(value?: string): string {
  const trimmed = value?.replace(/TRIGGER:\s*[^|]+/gi, '').replace(/\s+/g, ' ').trim();
  return trimmed && trimmed.length > 0
    ? trimmed
    : 'Structured questionnaire ready for preview, assignment, and manual completion.';
}

function toTitleCase(value: string | null | undefined): string {
  if (value == null || typeof value !== 'string') return 'Unknown';
  return value
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getQuestionnaireCodePrefix(value: string): string {
  const normalized = value.replace(/[^a-zA-Z0-9\s]/g, ' ').trim();
  if (!normalized) return 'QST';

  const tokens = normalized.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 'QST';

  if (isDietaryCodeLabel(value)) {
    return 'DIEA';
  }

  if (tokens.length === 1) {
    return tokens[0].slice(0, 3).toUpperCase();
  }

  return tokens
    .map((token) => token.charAt(0))
    .join('')
    .slice(0, 3)
    .toUpperCase();
}

export function normalizeQuestionnaireSourceLabel(value?: string): string {
  if (!value) return 'Untitled Questionnaire';

  const normalized = value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized.length > 0 ? normalized : 'Untitled Questionnaire';
}

function cleanSectionLabel(value?: string): string {
  const fallback = 'General';
  if (!value) return fallback;

  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized.length > 0 ? normalized : fallback;
}

function isDietaryCodeLabel(label: string): boolean {
  const normalized = label.replace(/[^a-zA-Z0-9\s]/g, ' ').trim();
  const [firstToken] = normalized.split(/\s+/).filter(Boolean);
  return firstToken?.toLowerCase() === 'dietary';
}

function getSectionShortCode(label: string): string {
  if (isDietaryCodeLabel(label)) {
    return 'DIEA';
  }

  return label
    .replace(/[^a-zA-Z0-9]+/g, '')
    .slice(0, 3)
    .toUpperCase() || 'QST';
}

export function isCustomLabeledQuestion(question: JsonQuestion): boolean {
  return cleanSectionLabel(question.label).toLowerCase() === 'custom';
}

export function isAdditionalQuestion(question: JsonQuestion): boolean {
  const normalizedLabel = cleanSectionLabel(question.label).toLowerCase();
  return question.isCustom === true || normalizedLabel === 'custom' || normalizedLabel === 'additional questions';
}

export function getQuestionnaireQuestionCount(section: JsonQuestionnaireSection): number {
  return getActiveQuestionCount(section);
}

export function getDurationLabel(section: JsonQuestionnaireSection): string {
  if (typeof section.estimatedTimeMinutes === 'number' && section.estimatedTimeMinutes > 0) {
    return `${section.estimatedTimeMinutes} minutes`;
  }

  const questionCount = getQuestionnaireQuestionCount(section);
  if (questionCount >= 40) return '10-15 minutes';
  if (questionCount >= 20) return '5-10 minutes';
  return '3-5 minutes';
}

export function getAssignedClientsCount(section: JsonQuestionnaireSection): number {
  return typeof section.assignedClientsCount === 'number' && Number.isFinite(section.assignedClientsCount)
    ? section.assignedClientsCount
    : 0;
}

export function getResponsesCount(section: JsonQuestionnaireSection): number {
  return typeof section.responsesCount === 'number' && Number.isFinite(section.responsesCount)
    ? section.responsesCount
    : 0;
}

function getSectionAppearance(label: string) {
  const normalized = label.toLowerCase();

  if (normalized.includes('consent')) {
    return {
      color: '#28b3d5',
      iconBg: '#dff5fb',
      iconColor: '#1699c8',
      icon: CheckRoundedIcon,
    };
  }

  if (normalized.includes('demographic')) {
    return {
      color: '#4c8df7',
      iconBg: '#e5efff',
      iconColor: '#4c8df7',
      icon: PersonRoundedIcon,
    };
  }

  if (normalized.includes('anthropometric')) {
    return {
      color: '#7c8798',
      iconBg: '#eef2f6',
      iconColor: '#7c8798',
      icon: StraightenRoundedIcon,
    };
  }

  if (normalized.includes('medical')) {
    return {
      color: '#ef6f72',
      iconBg: '#fee9ea',
      iconColor: '#ef6f72',
      icon: FavoriteBorderRoundedIcon,
    };
  }

  if (normalized.includes('goal')) {
    return {
      color: '#f2a32f',
      iconBg: '#fff2dd',
      iconColor: '#f2a32f',
      icon: FlagRoundedIcon,
    };
  }

  if (normalized.includes('lifestyle') || normalized.includes('exercise') || normalized.includes('sleep')) {
    return {
      color: '#e0b11a',
      iconBg: '#fff4ce',
      iconColor: '#e0b11a',
      icon: WavingHandRoundedIcon,
    };
  }

  if (
    normalized.includes('dietary') ||
    normalized.includes('diet') ||
    normalized.includes('breakfast') ||
    normalized.includes('lunch') ||
    normalized.includes('dinner') ||
    normalized.includes('hydration') ||
    normalized.includes('afternoon') ||
    normalized.includes('morning')
  ) {
    return {
      color: '#1cab71',
      iconBg: '#dff6ec',
      iconColor: '#1cab71',
      icon: LunchDiningRoundedIcon,
    };
  }

  if (normalized.includes('screen')) {
    return {
      color: '#8377ff',
      iconBg: '#ece9ff',
      iconColor: '#8377ff',
      icon: VisibilityRoundedIcon,
    };
  }

  if (normalized.includes('custom')) {
    return {
      color: '#4f8cff',
      iconBg: '#e7efff',
      iconColor: '#4f8cff',
      icon: TipsAndUpdatesRoundedIcon,
    };
  }

  return {
    color: '#6b7a8f',
    iconBg: '#edf3f8',
    iconColor: '#6b7a8f',
    icon: DescriptionRoundedIcon,
  };
}

export function buildSectionGroups(questions: JsonQuestion[]): QuestionnaireSectionGroup[] {
  const groups = new Map<
    string,
    {
      id: string;
      label: string;
      questions: JsonQuestion[];
      firstOrder: number;
    }
  >();

  const sortedQuestions = [...questions]
    .filter((question) => question.isActive !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  sortedQuestions.forEach((question) => {
    const label = cleanSectionLabel(question.label);
    const key = label.toLowerCase();
    const existing = groups.get(key);

    if (existing) {
      existing.questions.push(question);
      existing.firstOrder = Math.min(existing.firstOrder, question.order ?? Number.MAX_SAFE_INTEGER);
      return;
    }

    groups.set(key, {
      id: key.replace(/[^a-z0-9]+/g, '-'),
      label,
      questions: [question],
      firstOrder: question.order ?? Number.MAX_SAFE_INTEGER,
    });
  });

  return Array.from(groups.values())
    .sort((a, b) => a.firstOrder - b.firstOrder)
    .map((group) => {
      const appearance = getSectionAppearance(group.label);
      return {
        id: group.id,
        label: group.label,
        shortCode: getSectionShortCode(group.label),
        questions: group.questions,
        ...appearance,
      };
    });
}

export function buildNewCustomQuestionPayload(
  questionText: string,
  required: boolean,
  responseType: AdditionalQuestionResponseType,
  optionLines: string
): AddCustomQuestionToQuestionnairePayload {
  const options = responseTypeRequiresOptions(responseType)
    ? buildQuestionOptions(optionLines)
    : null;

  return {
    questionText,
    type: responseType,
    inputType: getResponseTypeInputType(responseType),
    options,
    placeholder: null,
    validation: required ? { required: true } : null,
    medicalConditions: [],
    gender: 'all',
    ageMin: null,
    ageMax: null,
    isConditional: false,
  };
}

export function getQuestionTypeLabel(type: JsonQuestion['type'] | null | undefined): string {
  if (type == null || type === '') return 'Unknown';
  switch (type) {
    case 'select':
      return 'Select one';
    case 'multi-select':
      return 'Select all that apply';
    case 'number-unit':
      return 'Number-unit';
    default:
      return toTitleCase(type);
  }
}

export function toAdditionalResponseType(type: JsonQuestion['type']): AdditionalQuestionResponseType {
  switch (type) {
    case 'text':
    case 'textarea':
    case 'number':
    case 'date':
    case 'select':
    case 'multi-select':
    case 'radio':
    case 'checkbox':
      return type;
    case 'number-unit':
    case 'scale':
      return 'number';
    case 'time':
      return 'text';
    default:
      return 'text';
  }
}

export function getQuestionTypeChipStyle(type: JsonQuestion['type']): QuestionTypeChipStyle {
  switch (type) {
    case 'checkbox':
      return { bgcolor: '#F2E7FF', color: '#7A28A8', borderColor: '#e5d4f5' };
    case 'select':
      return { bgcolor: '#D8FFEE', color: '#008060', borderColor: '#b8ecd9' };
    case 'multi-select':
      return { bgcolor: '#DEFFEC', color: '#22B24C', borderColor: '#c2f0dc' };
    case 'scale':
      return { bgcolor: '#FFF7E6', color: '#C46E00', borderColor: '#f5e6cc' };
    case 'radio':
      return { bgcolor: '#E7EFFF', color: '#4c8df7', borderColor: '#d0ddf9' };
    case 'number-unit':
      return { bgcolor: '#eef7fb', color: '#1699c8', borderColor: '#d0e8f2' };
    default:
      return { bgcolor: '#eef7fb', color: '#1699c8', borderColor: '#d0e8f2' };
  }
}

export function getQuestionMetaChips(question: JsonQuestion) {
  const chips: string[] = [];

  if (question.type === 'select' || question.type === 'multi-select' || question.type === 'radio' || question.type === 'checkbox') {
    const labels = (question.options ?? [])
      .map((option) => option.label?.trim())
      .filter((label): label is string => Boolean(label));

    chips.push(...labels.slice(0, 6));
    if (labels.length > 6) {
      chips.push(`+${labels.length - 6} more`);
    }
  } else if (question.type === 'number-unit') {
    const units = question.validation?.allowedUnits ?? [];
    chips.push(...units.slice(0, 4));
  } else if (question.placeholder) {
    chips.push(question.placeholder);
  }

  return chips;
}
